import React, { useState, useEffect, useCallback } from 'react';
import { ConfirmModal } from '../ui/ConfirmModal';
import { getArtists, createArtist, updateArtist, deleteArtist } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import { ArtistFormDialog } from './artists/ArtistFormDialog';
import { ArtistCard } from './artists/ArtistCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ArtistsManager = () => {
  const { toast } = useToast();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, artist: null });
  const [formData, setFormData] = useState({
    name: '', bio: '', image: '', soundcloudUrl: '', externalLink: '',
    instagram: '', facebook: '', spotify: '', youtube: '', bandcamp: '', type: []
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [customType, setCustomType] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const fetchArtists = useCallback(async () => {
    try { setArtists((await getArtists()).data); }
    catch { toast({ title: 'Error', description: 'Failed to fetch artists', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchArtists(); }, [fetchArtists]);

  const resetForm = () => {
    setFormData({ name: '', bio: '', image: '', soundcloudUrl: '', externalLink: '', instagram: '', facebook: '', spotify: '', youtube: '', bandcamp: '', type: [] });
    setImagePreview('');
    setEditingArtist(null);
    setCustomType('');
    setShowCustomInput(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'image') setImagePreview(e.target.value);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData({ ...formData, image: response.data.url });
      setImagePreview(response.data.url);
      toast({ title: 'Success', description: 'Image uploaded successfully' });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let finalTypes = [...formData.type];
      if (showCustomInput && customType.trim()) {
        finalTypes = [...finalTypes, ...customType.split(',').map(t => t.trim()).filter(Boolean)];
      }
      finalTypes = [...new Set(finalTypes)];
      const submitData = { ...formData, type: finalTypes };
      
      if (editingArtist) {
        await updateArtist(editingArtist.id, submitData);
        toast({ title: 'Success', description: 'Artist updated successfully' });
      } else {
        await createArtist(submitData);
        toast({ title: 'Success', description: 'Artist created successfully' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchArtists();
    } catch { toast({ title: 'Error', description: 'Failed to save artist', variant: 'destructive' }); }
  };

  const handleEdit = (artist) => {
    setEditingArtist(artist);
    let artistTypes = artist.type ? (Array.isArray(artist.type) ? artist.type : [artist.type]) : [];
    const standardTypes = ['DJ', 'Producer'];
    const customTypes = artistTypes.filter(t => !standardTypes.includes(t));
    const hasCustom = customTypes.length > 0;
    setFormData({
      name: artist.name, bio: artist.bio, image: artist.image,
      soundcloudUrl: artist.soundcloudUrl, externalLink: artist.externalLink || '',
      instagram: artist.instagram || '', facebook: artist.facebook || '',
      spotify: artist.spotify || '', youtube: artist.youtube || '',
      bandcamp: artist.bandcamp || '',
      type: hasCustom ? artistTypes.filter(t => standardTypes.includes(t)) : artistTypes
    });
    setCustomType(hasCustom ? customTypes.join(', ') : '');
    setShowCustomInput(hasCustom);
    setImagePreview(artist.image);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteArtist(deleteModal.artist.id);
      toast({ title: 'Success', description: 'Artist deleted successfully' });
      fetchArtists();
    } catch { toast({ title: 'Error', description: 'Failed to delete artist', variant: 'destructive' }); }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">Artists</h1>
          <p className="text-gray-400 mt-2">Manage your artist roster</p>
        </div>
        <ArtistFormDialog
          isOpen={isDialogOpen}
          onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}
          editingArtist={editingArtist}
          formData={formData} setFormData={setFormData}
          imagePreview={imagePreview} uploading={uploading}
          customType={customType} setCustomType={setCustomType}
          showCustomInput={showCustomInput} setShowCustomInput={setShowCustomInput}
          handleChange={handleChange} handleFileUpload={handleFileUpload} handleSubmit={handleSubmit}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {artists.map((artist) => (
          <ArtistCard key={artist.id} artist={artist}
            onEdit={() => handleEdit(artist)}
            onDelete={() => setDeleteModal({ isOpen: true, artist })}
          />
        ))}
      </div>

      <ConfirmModal isOpen={deleteModal.isOpen} onClose={() => setDeleteModal({ isOpen: false, artist: null })}
        onConfirm={handleDelete} title="Confirm Delete"
        message={`Are you sure you want to delete the artist "${deleteModal.artist?.name}"? This action cannot be undone.`}
        confirmText="Delete" cancelText="Cancel" />
    </div>
  );
};
